import React, { useState, useEffect } from 'react';
import { PageSpeedResult } from '../types';
import LlmAnalysisSectionV2Enhanced from '../components/v2/dashboard/LlmAnalysisSectionV2Enhanced';
import HallOfFameModalAppV2 from '../components/v2/dashboard/HallOfFameModalAppV2';
import ReportDownloadV2 from '../components/v2/dashboard/ReportDownloadV2';
import { calculateOverallRating, calculateMobileRating, calculateDesktopRating, getRatingGrade, getRatingColor } from '../utils/overallRating';
import { getImportantOpportunities } from '../utils/dataTransformers';
import { translateOpportunityTitle, translateDescription } from '../utils/reportGenerators';

// V2 디자인 시스템 (AppV2와 동일)
const designSystem = {
  colors: {
    primary: '#2467E7',
    primaryDark: '#1B4DB8',
    primaryLight: '#E8F2FF',
    secondary: '#6C5CE7',
    accent: '#00D4FF',
    success: '#00B894',
    warning: '#FDCB6E',
    danger: '#E17055',
    bgPrimary: '#FAFBFC',
    bgSecondary: '#FFFFFF',
    textPrimary: '#1A202C',
    textSecondary: '#718096',
    textMuted: '#A0AEC0',
    border: '#E2E8F0',
    overlay: 'rgba(0, 0, 0, 0.6)'
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.12)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.15)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.2)',
    '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)'
  },
  typography: {
    h1: { fontSize: '3rem', fontWeight: '900', lineHeight: '1.1' },
    h2: { fontSize: '2.25rem', fontWeight: '700', lineHeight: '1.2' },
    h3: { fontSize: '1.875rem', fontWeight: '600', lineHeight: '1.3' },
    h4: { fontSize: '1.25rem', fontWeight: '600', lineHeight: '1.4' },
    body: { fontSize: '1rem', fontWeight: '400', lineHeight: '1.6' },
    bodyLarge: { fontSize: '1.125rem', fontWeight: '400', lineHeight: '1.7' },
    bodySmall: { fontSize: '0.875rem', fontWeight: '400', lineHeight: '1.4' },
    caption: { fontSize: '0.75rem', fontWeight: '500', lineHeight: '1.3' }
  }
} as const;

/**
 * V2 명예의전당 보고서 전용 페이지
 * AppV2의 분석 결과 섹션을 그대로 계승
 * URL 쿼리 파라미터로 siteId를 받아서 해당 사이트의 보고서를 독립적으로 표시
 * 사용 예: /?report=hall-of-fame&v2=true&siteId=1
 */
const HallOfFameReportV2: React.FC = () => {
  // URL 쿼리 파라미터에서 siteId 추출
  const urlParams = new URLSearchParams(window.location.search);
  const siteId = urlParams.get('siteId');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<{
    url: string;
    siteName: string;
    pageSpeedData: PageSpeedResult;
    llmAnalysis: string | null;
    services: any[];
    customRatings?: { mobile: number; desktop: number; overall: number };
  } | null>(null);

  // 성능 점수 상태 (명예의전당 모달용)
  const [performanceScores, setPerformanceScores] = useState<{ desktop: number; mobile: number }>({ desktop: 0, mobile: 0 });

  // 명예의전당 모달 상태 관리
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [modalOrigin, setModalOrigin] = useState({ x: 50, y: 50 });

  // 개선 권장사항 탭 상태 관리
  const [opportunitiesTab, setOpportunitiesTab] = useState<'desktop' | 'mobile'>('desktop');

  // 리소스 상세 정보 펼침/접힘 상태 관리
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: { [key: number]: boolean } }>({});

  useEffect(() => {
    const loadReport = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log(`📊 V2 명예의전당 보고서 페이지 로드: siteId=${siteId}`);

        const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';
        const response = await fetch(`${BACKEND_URL}/api/hall-of-fame/report/${siteId}?includeLLM=true`);

        if (!response.ok) {
          throw new Error('보고서를 불러올 수 없습니다.');
        }

        const result = await response.json();

        if (result.success && result.analysis) {
          const { pageSpeedData, llmAnalysis, url, siteName, services, customRatings } = result.analysis;

          console.log(`✅ V2 명예의전당 보고서 로드 성공:`, siteName);
          console.log(`📊 커스텀 성능 점수:`, customRatings);

          setReportData({
            url: url,
            siteName: siteName,
            pageSpeedData: pageSpeedData,
            llmAnalysis: llmAnalysis,
            services: services || [],
            customRatings: customRatings
          });

          // 성능 점수 설정
          if (customRatings) {
            setPerformanceScores({
              desktop: customRatings.desktop,
              mobile: customRatings.mobile
            });
          }

          // 페이지 타이틀 설정
          document.title = `V2 보고서 - ${siteName}`;
        } else {
          throw new Error(result.error || '보고서 형식이 올바르지 않습니다.');
        }
      } catch (error: any) {
        console.error('❌ V2 명예의전당 보고서 로드 실패:', error);
        setError(`보고서를 불러올 수 없습니다: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (siteId) {
      loadReport();
    }
  }, [siteId]);

  // 도메인 추출 함수 (AppV2와 동일)
  const extractDomain = (url: string): string => {
    try {
      let domain = url;
      domain = domain.replace(/^https?:\/\//, '');
      domain = domain.replace(/^www\./, '');
      domain = domain.split('/')[0];
      domain = domain.split(':')[0];
      return domain;
    } catch (error) {
      return 'unknown-domain';
    }
  };

  // HTML 콘텐츠 생성 함수 (AppV2와 동일)
  const generateResourcesHTML = (filteredItems: any[], opportunityTitle: string, opportunityDescription: string, analysisUrl: string, includeDownloadButton = false) => {
    const domain = extractDomain(analysisUrl);
    const title = translateOpportunityTitle(opportunityTitle).replace(/[^a-zA-Z0-9\s\-\_]/g, '').replace(/\s+/g, '_');
    const date = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2, '0')}${String(new Date().getDate()).padStart(2, '0')}`;
    const fileName = `${domain}_${title}_${date}.html`;

    return `
<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${translateOpportunityTitle(opportunityTitle)} - 전체 리소스 목록</title>
  <style>
    body {
      font-family: 'Pretendard', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif;
      margin: 0;
      padding: 20px;
      background: linear-gradient(135deg, ${designSystem.colors.bgPrimary} 0%, ${designSystem.colors.border} 100%);
      min-height: 100vh;
    }
    .container {
      max-width: 1200px;
      margin: 0 auto;
      background: white;
      border-radius: 1rem;
      box-shadow: ${designSystem.shadows.xl};
      overflow: hidden;
    }
    .header {
      background: linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%);
      color: white;
      padding: 2rem;
      text-align: center;
      position: relative;
    }
    .header h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.75rem;
      font-weight: 700;
    }
    .header p {
      margin: 0;
      font-size: 1rem;
      opacity: 0.9;
    }
    .download-info {
      position: absolute;
      top: 15px;
      right: 20px;
      background: rgba(255, 255, 255, 0.2);
      padding: 0.5rem 0.75rem;
      border-radius: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.8;
    }
    .download-button {
      position: absolute;
      top: 15px;
      left: 20px;
      background: ${designSystem.colors.success};
      color: white;
      border: none;
      padding: 0.625rem 1rem;
      border-radius: 0.625rem;
      cursor: pointer;
      font-weight: 600;
      font-size: 0.8125rem;
      transition: all 0.2s ease;
      box-shadow: ${designSystem.shadows.md};
    }
    .download-button:hover {
      background: #00966B;
      transform: translateY(-1px);
      box-shadow: ${designSystem.shadows.lg};
    }
    .content {
      padding: 2rem;
    }
    .resource-item {
      background: ${designSystem.colors.bgPrimary};
      border: 1px solid ${designSystem.colors.border};
      border-radius: 0.75rem;
      margin-bottom: 1rem;
      padding: 1.25rem;
      transition: all 0.2s ease;
    }
    .resource-item:hover {
      transform: translateY(-2px);
      box-shadow: ${designSystem.shadows.md};
    }
    .resource-number {
      background: ${designSystem.colors.primary};
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 1.25rem;
      font-size: 0.75rem;
      font-weight: 600;
      display: inline-block;
      margin-bottom: 0.625rem;
    }
    .resource-url {
      font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
      background: white;
      border: 1px solid ${designSystem.colors.border};
      border-radius: 0.5rem;
      padding: 0.9375rem;
      word-break: break-all;
      line-height: 1.5;
      color: ${designSystem.colors.primary};
      text-decoration: none;
      display: block;
      transition: all 0.2s ease;
    }
    .resource-url:hover {
      background: ${designSystem.colors.primaryLight};
      border-color: ${designSystem.colors.primary};
      color: ${designSystem.colors.primaryDark};
    }
    .stats {
      background: linear-gradient(135deg, ${designSystem.colors.primaryLight} 0%, rgba(36, 103, 231, 0.1) 100%);
      padding: 1.25rem;
      text-align: center;
      color: ${designSystem.colors.primaryDark};
      font-weight: 600;
      margin: 1.5rem 0;
      border-radius: 0.75rem;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      ${includeDownloadButton ? `<button class="download-button" onclick="downloadAsHTML()">다운로드</button>` : ''}
      <div class="download-info">${fileName}</div>
      <h1>${translateOpportunityTitle(opportunityTitle)}</h1>
      <p>${analysisUrl}</p>
    </div>
    <div class="stats">
      총 ${filteredItems.length}개 리소스
    </div>
    <div class="content">
      ${filteredItems.map((item: any, index: number) => `
        <div class="resource-item">
          <div class="resource-number">#${index + 1}</div>
          <a href="${item.url}" target="_blank" class="resource-url">${item.url}</a>
        </div>
      `).join('')}
    </div>
  </div>
  ${includeDownloadButton ? `
  <script>
    function downloadAsHTML() {
      const htmlContent = document.documentElement.outerHTML;
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '${fileName}';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  </script>
  ` : ''}
</body>
</html>
    `.trim();
  };

  // 새 창에서 리소스 보기 함수 (AppV2와 동일)
  const openResourcesInNewWindow = (filteredItems: any[], opportunityTitle: string, opportunityDescription: string, analysisUrl: string) => {
    const htmlContent = generateResourcesHTML(filteredItems, opportunityTitle, opportunityDescription, analysisUrl, true);
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    }
  };

  // 리소스 상세 정보 렌더링 함수 (AppV2와 동일)
  const renderResourceDetails = (details: any, itemId: string, opportunityTitle: string, opportunityDescription: string) => {
    if (!details || !details.items || details.items.length === 0) {
      return null;
    }

    const itemKey = itemId;
    const filteredItems = details.items.slice(0, 5);
    const hasMoreItems = details.items.length > 5;
    const isExpanded = expandedItems[itemKey]?.[0] || false;

    return (
      <div style={{
        marginTop: '1rem',
        padding: '1rem',
        background: `${designSystem.colors.bgPrimary}`,
        borderRadius: '0.75rem',
        border: `1px solid ${designSystem.colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem'
        }}>
          <span style={{
            ...designSystem.typography.bodySmall,
            fontWeight: '600',
            color: designSystem.colors.textSecondary
          }}>
            관련 리소스
          </span>
          <span style={{
            ...designSystem.typography.caption,
            color: designSystem.colors.textMuted,
            background: 'white',
            padding: '0.25rem 0.75rem',
            borderRadius: '1rem'
          }}>
            {details.items.length}개
          </span>
        </div>

        {filteredItems.map((item: any, index: number) => (
          <div key={index} style={{
            marginBottom: index < filteredItems.length - 1 ? '0.5rem' : '0'
          }}>
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...designSystem.typography.bodySmall,
                color: designSystem.colors.primary,
                textDecoration: 'none',
                wordBreak: 'break-all',
                display: 'block',
                padding: '0.5rem 0.75rem',
                background: 'white',
                borderRadius: '0.5rem',
                border: `1px solid ${designSystem.colors.border}`,
                transition: 'all 0.2s ease',
                fontFamily: "'Consolas', 'Monaco', monospace",
                fontSize: '0.8125rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = designSystem.colors.primaryLight;
                e.currentTarget.style.borderColor = designSystem.colors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = designSystem.colors.border;
              }}
            >
              {index + 1}. {item.url}
            </a>
          </div>
        ))}

        {hasMoreItems && (
          <button
            onClick={() => openResourcesInNewWindow(details.items, opportunityTitle, opportunityDescription, reportData?.url || '')}
            style={{
              marginTop: '0.75rem',
              width: '100%',
              padding: '0.625rem 1rem',
              background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '0.625rem',
              ...designSystem.typography.bodySmall,
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: designSystem.shadows.sm
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.md;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.sm;
            }}
          >
            전체 리소스 보기 ({details.items.length}개)
          </button>
        )}
      </div>
    );
  };

  // 로딩 중 - AppV2 스타일
  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: designSystem.colors.bgSecondary,
        fontFamily: "'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>
        <section style={{
          padding: '3rem 2rem',
          background: designSystem.colors.bgSecondary,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            maxWidth: '800px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '3rem',
              boxShadow: designSystem.shadows.xl
            }}>
              {/* 로딩 애니메이션 */}
              <div style={{
                width: '80px',
                height: '80px',
                border: `8px solid ${designSystem.colors.border}`,
                borderTop: `8px solid ${designSystem.colors.primary}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 2rem'
              }} />

              <h2 style={{
                ...designSystem.typography.h2,
                color: designSystem.colors.primary,
                marginBottom: '1rem'
              }}>
                명예의전당 보고서 로딩 중...
              </h2>

              <p style={{
                ...designSystem.typography.bodyLarge,
                color: designSystem.colors.textSecondary,
                marginBottom: '2rem'
              }}>
                성능 데이터를 불러오고 있습니다...
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                alignItems: 'center'
              }}>
                <div style={{
                  ...designSystem.typography.body,
                  color: designSystem.colors.textMuted
                }}>
                  AI 분석이 포함되어 약 5-7초 소요됩니다
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 애니메이션 스타일 */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // 에러 발생 - AppV2 스타일
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: designSystem.colors.bgSecondary,
        fontFamily: "'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '2rem',
          padding: '3rem',
          boxShadow: designSystem.shadows.xl,
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            margin: '0 auto 2rem',
            background: `linear-gradient(135deg, ${designSystem.colors.danger}20 0%, ${designSystem.colors.danger}10 100%)`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '60px'
          }}>
            ❌
          </div>

          <h2 style={{
            ...designSystem.typography.h2,
            color: designSystem.colors.danger,
            marginBottom: '1rem'
          }}>
            보고서 로드 실패
          </h2>

          <p style={{
            ...designSystem.typography.body,
            color: designSystem.colors.textSecondary,
            marginBottom: '2rem',
            lineHeight: '1.6'
          }}>
            {error}
          </p>

          <button
            onClick={() => window.close()}
            style={{
              padding: '0.875rem 2rem',
              background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 100%)`,
              color: 'white',
              border: 'none',
              borderRadius: '1rem',
              ...designSystem.typography.body,
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: designSystem.shadows.lg,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.lg;
            }}
          >
            닫기
          </button>
        </div>
      </div>
    );
  }

  // 보고서 표시 - AppV2 분석 결과 섹션 스타일
  if (!reportData) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: designSystem.colors.bgPrimary,
      fontFamily: "'Inter', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, sans-serif"
    }}>
      {/* V2 스타일 헤더 - 축소 버전 */}
      <div style={{
        background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.primaryDark} 50%, ${designSystem.colors.secondary} 100%)`,
        boxShadow: designSystem.shadows.xl,
        padding: '1.25rem 2rem',
        marginBottom: '0',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: `1px solid ${designSystem.colors.border}`,
        backdropFilter: 'blur(20px)'
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem',
          flexWrap: 'wrap'
        }}>
          {/* 왼쪽: 보고서 정보 (사이트명과 URL 한줄) */}
          <div style={{
            flex: 1,
            minWidth: '300px',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}>
            <h1 style={{
              ...designSystem.typography.h3,
              color: 'white',
              margin: 0,
              fontWeight: '700',
              fontSize: '1.5rem'
            }}>
              {reportData.siteName}
            </h1>
            <a
              href={reportData.url.startsWith('http') ? reportData.url : `https://${reportData.url}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                ...designSystem.typography.bodySmall,
                color: 'rgba(255, 255, 255, 0.85)',
                textDecoration: 'none',
                fontFamily: 'Consolas, Monaco, monospace',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                padding: '0.375rem 0.875rem',
                borderRadius: '0.5rem',
                display: 'inline-block',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.85)';
              }}
            >
              🌐 {reportData.url}
            </a>
          </div>

          {/* 오른쪽: 다운로드 버튼 + 닫기 버튼 */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <ReportDownloadV2
              pageSpeedData={reportData.pageSpeedData}
              url={reportData.url}
              llmAnalysis={reportData.llmAnalysis}
              customRatings={reportData.customRatings}
            />
            <button
              onClick={() => window.close()}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '0.875rem',
                ...designSystem.typography.bodySmall,
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 분석 결과 섹션 - AppV2 스타일 그대로 */}
      <section style={{
        padding: '4rem 2rem',
        background: designSystem.colors.bgPrimary
      }}>
        <div style={{
          maxWidth: '1600px',
          margin: '0 auto'
        }}>
          {/* 4개 핵심 메트릭 카드 그리드 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem',
            marginBottom: '4rem'
          }}>
            {/* 핵심 지표 (AppV2 스타일) */}
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '2.5rem',
              boxShadow: designSystem.shadows.lg,
              border: `1px solid ${designSystem.colors.border}`,
              textAlign: 'center',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.lg;
            }}>
              <div style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                borderRadius: '1rem',
                backgroundColor: `${designSystem.colors.primary}20`,
                marginBottom: '1.5rem',
                alignSelf: 'center'
              }}>
                <h3 style={{
                  ...designSystem.typography.h3,
                  color: designSystem.colors.primary,
                  margin: 0
                }}>
                  핵심 지표
                </h3>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1
              }}>
                {/* 첫 콘텐츠 표시 (FCP) */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingBottom: '1.5rem',
                  borderBottom: `1px solid ${designSystem.colors.border}`
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: designSystem.colors.primary
                  }}>
                    {reportData.pageSpeedData.mobile?.firstContentfulPaint
                      ? Math.round(reportData.pageSpeedData.mobile.firstContentfulPaint * 10) / 10
                      : '—'}s
                  </div>
                  <div style={{
                    ...designSystem.typography.caption,
                    color: designSystem.colors.textSecondary,
                    marginTop: '0.5rem'
                  }}>
                    첫 콘텐츠 표시
                  </div>
                </div>

                {/* 최대 콘텐츠 표시 (LCP) */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingTop: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: designSystem.colors.primary
                  }}>
                    {reportData.pageSpeedData.mobile?.largestContentfulPaint
                      ? Math.round(reportData.pageSpeedData.mobile.largestContentfulPaint * 10) / 10
                      : '—'}s
                  </div>
                  <div style={{
                    ...designSystem.typography.caption,
                    color: designSystem.colors.textSecondary,
                    marginTop: '0.5rem'
                  }}>
                    최대 콘텐츠 표시
                  </div>
                </div>
              </div>
            </div>

            {/* 성능 점수 */}
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '2.5rem',
              boxShadow: designSystem.shadows.lg,
              border: `1px solid ${designSystem.colors.border}`,
              textAlign: 'center',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.lg;
            }}>
              <div style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                borderRadius: '1rem',
                backgroundColor: `${getRatingColor(reportData.customRatings?.overall || 0)}20`,
                marginBottom: '1.5rem',
                alignSelf: 'center'
              }}>
                <h3 style={{
                  ...designSystem.typography.h3,
                  color: getRatingColor(reportData.customRatings?.overall || 0),
                  margin: 0
                }}>
                  성능 점수
                </h3>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1
              }}>
                {/* 데스크톱 점수 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingBottom: '1.5rem',
                  borderBottom: `1px solid ${designSystem.colors.border}`
                }}>
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '900',
                    color: getRatingColor(reportData.customRatings?.desktop || 0),
                    lineHeight: '1'
                  }}>
                    {Math.round(reportData.customRatings?.desktop || 0)}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: getRatingColor(reportData.customRatings?.desktop || 0),
                    marginTop: '0.5rem',
                    marginBottom: '0.25rem'
                  }}>
                    {getRatingGrade(reportData.customRatings?.desktop || 0)}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: designSystem.colors.textMuted
                  }}>
                    데스크톱
                  </div>
                </div>

                {/* 모바일 점수 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingTop: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '900',
                    color: getRatingColor(reportData.customRatings?.mobile || 0),
                    lineHeight: '1'
                  }}>
                    {Math.round(reportData.customRatings?.mobile || 0)}
                  </div>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: getRatingColor(reportData.customRatings?.mobile || 0),
                    marginTop: '0.5rem',
                    marginBottom: '0.25rem'
                  }}>
                    {getRatingGrade(reportData.customRatings?.mobile || 0)}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: designSystem.colors.textMuted
                  }}>
                    모바일
                  </div>
                </div>
              </div>
            </div>

            {/* 페이지 크기 */}
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '2.5rem',
              boxShadow: designSystem.shadows.lg,
              border: `1px solid ${designSystem.colors.border}`,
              textAlign: 'center',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.lg;
            }}>
              <div style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                borderRadius: '1rem',
                backgroundColor: `${designSystem.colors.secondary}20`,
                marginBottom: '1.5rem',
                alignSelf: 'center'
              }}>
                <h3 style={{
                  ...designSystem.typography.h3,
                  color: designSystem.colors.secondary,
                  margin: 0
                }}>
                  페이지 크기
                </h3>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: '1.5rem'
              }}>
                {/* 데스크톱 정보 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  paddingBottom: '1.5rem',
                  borderBottom: `1px solid ${designSystem.colors.border}`
                }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: designSystem.colors.secondary,
                    marginBottom: '1rem'
                  }}>
                    {((reportData.pageSpeedData.desktop?.pageSize?.totalSize || 0) / (1024 * 1024)).toFixed(2)}MB
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: designSystem.colors.textSecondary,
                      marginLeft: '0.5rem'
                    }}>
                      (데스크톱)
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem',
                    fontSize: '0.875rem'
                  }}>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>리소스:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{reportData.pageSpeedData.desktop?.pageSize?.resourceCount || 0}개</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>CSS:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.desktop?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['stylesheet'] || breakdown['Stylesheet'] || breakdown['css'] || breakdown['CSS'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>스크립트:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.desktop?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['script'] || breakdown['Script'] || breakdown['javascript'] || breakdown['JavaScript'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>이미지:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.desktop?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['image'] || breakdown['Image'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                  </div>
                </div>

                {/* 모바일 정보 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: designSystem.colors.secondary,
                    marginBottom: '1rem'
                  }}>
                    {((reportData.pageSpeedData.mobile?.pageSize?.totalSize || 0) / (1024 * 1024)).toFixed(2)}MB
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: designSystem.colors.textSecondary,
                      marginLeft: '0.5rem'
                    }}>
                      (모바일)
                    </span>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem',
                    fontSize: '0.875rem'
                  }}>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>리소스:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{reportData.pageSpeedData.mobile?.pageSize?.resourceCount || 0}개</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>CSS:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.mobile?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['stylesheet'] || breakdown['Stylesheet'] || breakdown['css'] || breakdown['CSS'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>스크립트:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.mobile?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['script'] || breakdown['Script'] || breakdown['javascript'] || breakdown['JavaScript'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.75rem',
                      backgroundColor: `${designSystem.colors.secondary}15`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ color: designSystem.colors.secondary, fontWeight: '600' }}>이미지:</span>
                      <span style={{ color: designSystem.colors.textPrimary, fontWeight: '600' }}>{(() => {
                        const breakdown = reportData.pageSpeedData.mobile?.pageSize?.resourceBreakdown || {};
                        const bytes = breakdown['image'] || breakdown['Image'] || 0;
                        return bytes > 1024 * 1024 ? (bytes / (1024 * 1024)).toFixed(1) + 'MB' : (bytes / 1024).toFixed(0) + 'KB';
                      })()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 차단 시간 */}
            <div style={{
              background: 'white',
              borderRadius: '2rem',
              padding: '2.5rem',
              boxShadow: designSystem.shadows.lg,
              border: `1px solid ${designSystem.colors.border}`,
              textAlign: 'center',
              transition: 'all 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = designSystem.shadows.xl;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = designSystem.shadows.lg;
            }}>
              <div style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                borderRadius: '1rem',
                backgroundColor: `${designSystem.colors.warning}20`,
                marginBottom: '1.5rem',
                alignSelf: 'center'
              }}>
                <h3 style={{
                  ...designSystem.typography.h3,
                  color: designSystem.colors.warning,
                  margin: 0
                }}>
                  총 차단 시간
                </h3>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1
              }}>
                {/* 데스크톱 정보 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingBottom: '1.5rem',
                  borderBottom: `1px solid ${designSystem.colors.border}`
                }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: designSystem.colors.warning
                  }}>
                    {Math.round(reportData.pageSpeedData.desktop?.totalBlockingTime || 0)}ms
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: designSystem.colors.textSecondary,
                      marginLeft: '0.5rem'
                    }}>
                      (데스크톱)
                    </span>
                  </div>
                </div>

                {/* 모바일 정보 */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  paddingTop: '1.5rem'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: designSystem.colors.warning
                  }}>
                    {Math.round(reportData.pageSpeedData.mobile?.totalBlockingTime || 0)}ms
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: designSystem.colors.textSecondary,
                      marginLeft: '0.5rem'
                    }}>
                      (모바일)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI 분석 섹션 - V2Enhanced 컴포넌트 사용 */}
          {reportData.llmAnalysis && reportData.pageSpeedData && (
            <LlmAnalysisSectionV2Enhanced
              pageSpeedData={reportData.pageSpeedData}
              url={reportData.url}
              llmAnalysis={typeof reportData.llmAnalysis === 'string'
                ? reportData.llmAnalysis
                : String(reportData.llmAnalysis)}
              isLlmLoading={false}
              showHallOfFame={showHallOfFame}
              setShowHallOfFame={setShowHallOfFame}
              setModalOrigin={setModalOrigin}
            />
          )}

          {/* 개선 권장사항 - 실제 데이터 기반 탭 방식 */}
          {(() => {
            const desktopOpportunities = reportData.pageSpeedData?.desktop?.opportunities
              ? getImportantOpportunities(reportData.pageSpeedData.desktop.opportunities)
              : [];
            const mobileOpportunities = reportData.pageSpeedData?.mobile?.opportunities
              ? getImportantOpportunities(reportData.pageSpeedData.mobile.opportunities)
              : [];

            // 데스크톱이나 모바일 중 하나라도 데이터가 있을 때만 표시
            if (desktopOpportunities.length === 0 && mobileOpportunities.length === 0) {
              return null;
            }

            return (
              <div style={{
                marginTop: '4rem'
              }}>
                <h2 style={{
                  ...designSystem.typography.h2,
                  color: designSystem.colors.primary,
                  textAlign: 'center',
                  marginBottom: '2rem'
                }}>
                  핵심 성능 개선 기회
                </h2>

                {/* 탭 버튼 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <button
                    onClick={() => setOpportunitiesTab('desktop')}
                    style={{
                      padding: '0.75rem 2rem',
                      borderRadius: '1rem',
                      border: `2px solid ${opportunitiesTab === 'desktop' ? designSystem.colors.success : designSystem.colors.border}`,
                      backgroundColor: opportunitiesTab === 'desktop' ? designSystem.colors.success : 'white',
                      color: opportunitiesTab === 'desktop' ? 'white' : designSystem.colors.textPrimary,
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: opportunitiesTab === 'desktop' ? designSystem.shadows.md : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (opportunitiesTab !== 'desktop') {
                        e.currentTarget.style.backgroundColor = designSystem.colors.bgPrimary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (opportunitiesTab !== 'desktop') {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    데스크톱 ({desktopOpportunities.length})
                  </button>
                  <button
                    onClick={() => setOpportunitiesTab('mobile')}
                    style={{
                      padding: '0.75rem 2rem',
                      borderRadius: '1rem',
                      border: `2px solid ${opportunitiesTab === 'mobile' ? designSystem.colors.warning : designSystem.colors.border}`,
                      backgroundColor: opportunitiesTab === 'mobile' ? designSystem.colors.warning : 'white',
                      color: opportunitiesTab === 'mobile' ? 'white' : designSystem.colors.textPrimary,
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: opportunitiesTab === 'mobile' ? designSystem.shadows.md : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (opportunitiesTab !== 'mobile') {
                        e.currentTarget.style.backgroundColor = designSystem.colors.bgPrimary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (opportunitiesTab !== 'mobile') {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    모바일 ({mobileOpportunities.length})
                  </button>
                </div>

                {/* 탭 컨텐츠 */}
                <div>
                  {opportunitiesTab === 'desktop' && desktopOpportunities.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gap: '1.5rem'
                    }}>
                      {desktopOpportunities.slice(0, 5).map((opportunity: any, index: number) => (
                        <div key={index} style={{
                          background: 'white',
                          borderRadius: '1.5rem',
                          padding: '2rem',
                          boxShadow: designSystem.shadows.lg,
                          border: `2px solid ${designSystem.colors.success}30`,
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = designSystem.shadows.xl;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                        }}>
                          <h3 style={{
                            ...designSystem.typography.h3,
                            color: designSystem.colors.success,
                            marginBottom: '1rem',
                            fontSize: '1.25rem'
                          }}>
                            {translateOpportunityTitle(opportunity.title)}
                          </h3>
                          <p
                            style={{
                              ...designSystem.typography.body,
                              color: designSystem.colors.textSecondary,
                              lineHeight: '1.6',
                              marginBottom: opportunity.details ? '1rem' : '0'
                            }}
                            dangerouslySetInnerHTML={{ __html: translateDescription(opportunity.description) }}
                          />
                          {renderResourceDetails(
                            opportunity.details,
                            `desktop-${opportunity.id || index}`,
                            opportunity.title,
                            opportunity.description
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {opportunitiesTab === 'mobile' && mobileOpportunities.length > 0 && (
                    <div style={{
                      display: 'grid',
                      gap: '1.5rem'
                    }}>
                      {mobileOpportunities.slice(0, 5).map((opportunity: any, index: number) => (
                        <div key={index} style={{
                          background: 'white',
                          borderRadius: '1.5rem',
                          padding: '2rem',
                          boxShadow: designSystem.shadows.lg,
                          border: `2px solid ${designSystem.colors.warning}30`,
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = designSystem.shadows.xl;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = designSystem.shadows.lg;
                        }}>
                          <h3 style={{
                            ...designSystem.typography.h3,
                            color: designSystem.colors.warning,
                            marginBottom: '1rem',
                            fontSize: '1.25rem'
                          }}>
                            {translateOpportunityTitle(opportunity.title)}
                          </h3>
                          <p
                            style={{
                              ...designSystem.typography.body,
                              color: designSystem.colors.textSecondary,
                              lineHeight: '1.6',
                              marginBottom: opportunity.details ? '1rem' : '0'
                            }}
                            dangerouslySetInnerHTML={{ __html: translateDescription(opportunity.description) }}
                          />
                          {renderResourceDetails(
                            opportunity.details,
                            `mobile-${opportunity.id || index}`,
                            opportunity.title,
                            opportunity.description
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* 새로운 분석 버튼 */}
          <div style={{
            textAlign: 'center',
            marginTop: '4rem',
            padding: '2rem'
          }}>
            <button
              onClick={() => window.close()}
              style={{
                background: `linear-gradient(135deg, ${designSystem.colors.primary} 0%, ${designSystem.colors.secondary} 100%)`,
                color: 'white',
                border: 'none',
                borderRadius: '2rem',
                padding: '1rem 2rem',
                fontSize: '1.125rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: designSystem.shadows.lg,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = designSystem.shadows.xl;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = designSystem.shadows.lg;
              }}
            >
              닫기
            </button>
          </div>
        </div>
      </section>

      {/* 전역 스타일 */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* 명예의전당 모달 - AppV2 전용 새 디자인 */}
      <HallOfFameModalAppV2
        showHallOfFame={showHallOfFame}
        setShowHallOfFame={setShowHallOfFame}
        currentSiteUrl={reportData.url}
        currentSiteData={reportData.pageSpeedData}
        performanceScores={performanceScores}
      />
    </div>
  );
};

export default HallOfFameReportV2;
